import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NgModule, OnChanges, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ObjectType } from '@vendasta/auxiliary-data-components';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FieldEditorComponent, FieldEditorComponentModule } from '../field-editor/field-editor.component';
import { ObjectTypeOption, objectTypeOptions as _objectTypeOptions } from '../object-type-options';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { AuxDrawerComponent } from '../side-panel';

@Component({
  selector: 'aux-app-auxiliary-data-panel',
  template: `
    <div class="drawer-container">
      <div class="error-alert" *ngIf="reachedCreationLimit$ | async">
        <glxy-alert type="error">
          <strong>{{ 'ADMINISTRATION.CUSTOM_FIELDS.REACHED_CREATION_LIMIT' | translate }} —</strong>
          {{ 'ADMINISTRATION.CUSTOM_FIELDS.TOO_MANY_CUSTOM_FIELDS' | translate }}
        </glxy-alert>
      </div>

      <aux-field-editor
        #fieldEditor
        [createNewField]="creatingNewField$$ | async"
        [initialObjectType]="objectType"
        [showObjectTypeControl]="showObjectTypeControl"
        (changesCompleted)="selectObjectType($event)"
        (pendingChanges)="pendingChanges($event)"
        (saveDisabled)="saveDisabled($event)"
        (savingChanges)="savingChanges($event)"
      ></aux-field-editor>

      <div class="drawer-actions">
        <ng-container *ngIf="{ saveDisabled: savingDisabled$$ | async } as observed">
          <button
            (click)="save()"
            mat-flat-button
            [disabled]="(reachedCreationLimit$ | async) || observed.saveDisabled"
            color="primary"
            type="button"
            data-testid="save-button"
            *ngIf="(savingChanges$$ | async) === false"
          >
            {{ 'COMMON.ACTION_LABELS.SAVE' | translate }}
          </button>
          <span class="loading-spinner" *ngIf="savingChanges$$ | async">
            <mat-spinner [diameter]="34"></mat-spinner>
          </span>
        </ng-container>
        <button (click)="cancel()" mat-stroked-button color="secondary" type="button">
          {{ 'COMMON.ACTION_LABELS.CANCEL' | translate }}
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./auxiliary-data-panel.component.scss'],
  standalone: false,
})
export class AuxiliaryDataPanelComponent implements OnChanges {
  @ViewChild('fieldEditor', { static: false }) fieldEditor: FieldEditorComponent;
  @Input() createNewField = false;
  @Input() objectType: ObjectType = 'business';
  @Input() showObjectTypeControl = true;
  @Output() objectTypeSelected = new EventEmitter<ObjectType>();
  @Input() set drawer(drawer: AuxDrawerComponent) {
    this.drawerRef = drawer;
  }
  drawerRef: AuxDrawerComponent;
  objectTypeOptions: ObjectTypeOption[] = _objectTypeOptions;
  pendingChanges$$ = new BehaviorSubject<boolean>(false);
  savingDisabled$$ = new BehaviorSubject<boolean>(true);
  savingChanges$$ = new BehaviorSubject<boolean>(false);
  creatingNewField$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  createEditTitle$: Observable<string> = this.creatingNewField$$.pipe(
    map((creatingNew) => {
      if (creatingNew) {
        return 'COMMON.AUXILIARY_DATA.CREATE_HEADER';
      }
      return 'COMMON.AUXILIARY_DATA.EDIT_HEADER';
    }),
  );

  reachedCreationLimit$: Observable<boolean>;

  ngOnChanges(): void {
    this.creatingNewField$$.next(this.createNewField);
  }

  selectObjectType(type: ObjectType): void {
    this.objectTypeSelected.next(type);
  }

  save(): void {
    this.fieldEditor.save();
  }

  cancel(): void {
    this.fieldEditor.cancel();
    this.drawerRef.close();
  }

  saveDisabled(disabled: boolean): void {
    this.savingDisabled$$.next(disabled);
  }

  pendingChanges(pending: boolean): void {
    this.pendingChanges$$.next(pending);
  }

  savingChanges(saving: boolean): void {
    this.savingChanges$$.next(saving);
  }

  canDeactivate(): boolean {
    return !this.pendingChanges$$.value;
  }
}

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule,
    FieldEditorComponentModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatIcon,
    MatToolbar,
    MatToolbarRow,
  ],
  // AIM / SCAM patterns - only a single thing!
  declarations: [AuxiliaryDataPanelComponent],
  exports: [AuxiliaryDataPanelComponent],
})
export class AuxiliaryDataPanelComponentModule {}
